import React from 'react';
import { Building2, Building, ShoppingBag, Key, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import CTABanner from './CTABanner';

const services = [
  {
    id: 'residencial',
    title: 'Administración Residencial',
    description: 'Transformamos tu condominio en una comunidad inteligente y conectada',
    icon: 'Building2',
    features: [
      'Control de accesos con reconocimiento facial',
      'Gestión de gastos comunes digitalizados',
      'App móvil para residentes',
      'Reserva de espacios comunes online',
      'Gestión automatizada de visitas y deliveries',
      'Asesoría a Comités',
      'Planificación Anual',
      'Asesoría Legal',
      'Seguridad y Prevención',
      'Gobierno Corporativo',
      'Gestión de Proveedores',
      'Mantenimiento',
      'Atención al Cliente',
      'Finanzas y Contabilidad',
      'Recursos Humanos'
    ]
  },
  {
    id: 'corporativo',
    title: 'Edificios Corporativos',
    description: 'Soluciones empresariales que aumentan la eficiencia operacional',
    icon: 'Building',
    features: [
      'Gestión de accesos corporativos',
      'Reportes ejecutivos en tiempo real',
      'Control de gastos y presupuestos',
      'Mantenimiento preventivo automatizado',
      'Integración con sistemas empresariales',
      'Asesoría a Comités',
      'Planificación Anual',
      'Asesoría Legal',
      'Seguridad y Prevención',
      'Gobierno Corporativo',
      'Gestión de Proveedores',
      'Mantenimiento',
      'Atención al Cliente',
      'Finanzas y Contabilidad',
      'Recursos Humanos'
    ]
  },
  {
    id: 'comercial',
    title: 'Espacios Comerciales',
    description: 'Administración especializada para centros comerciales y retail',
    icon: 'ShoppingBag',
    features: [
      'Control de flujo de visitantes',
      'Gestión de locatarios y contratos',
      'Mantenimiento de áreas comunes',
      'Seguridad y prevención de riesgos',
      'Reportes de ocupación y desempeño',
      'Asesoría a Comités',
      'Planificación Anual',
      'Asesoría Legal',
      'Seguridad y Prevención',
      'Gobierno Corporativo',
      'Gestión de Proveedores',
      'Mantenimiento',
      'Atención al Cliente',
      'Finanzas y Contabilidad',
      'Recursos Humanos'
    ]
  },
  {
    id: 'propiedades',
    title: 'Corredores de Propiedades',
    description: 'Servicios integrales de venta y arriendo con tecnología',
    icon: 'Key',
    features: [
      'Plataforma digital de propiedades',
      'Gestión de arriendos automatizada',
      'Control de contratos y pagos',
      'Tours virtuales con IA',
      'CRM para seguimiento de clientes',
      'Asesoría a Comités',
      'Planificación Anual',
      'Asesoría Legal',
      'Seguridad y Prevención',
      'Gobierno Corporativo',
      'Gestión de Proveedores',
      'Mantenimiento',
      'Atención al Cliente',
      'Finanzas y Contabilidad',
      'Recursos Humanos'
    ]
  }
];

const iconMap = {
  Building2,
  Building,
  ShoppingBag,
  Key
};

const Services = () => {
  return (
    <section id="servicios" className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Building watermark background */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute bottom-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiMwODJFNUMiLz48L3N2Zz4=')] bg-bottom bg-repeat-x" style={{ backgroundSize: '200px 400px' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#082E5C] mb-4">
            Nuestros Servicios
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Administración integral con tecnología de punta para todo tipo de propiedades
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-[#00D4D8] bg-white"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#00D4D8] to-[#082E5C] rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl text-[#082E5C] group-hover:text-[#00D4D8] transition-colors mb-2">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-base text-gray-600">
                        {service.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle2 className="text-[#00D4D8] flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <CTABanner variant="compact" />
      </div>
    </section>
  );
};

export default Services;
